import React, { createContext, useContext, useReducer, useCallback, useRef } from "react";
import Swal from "sweetalert2";
import { getPipelines, getColumns, reorderColumns as reorderColumnsAPI, getBoardData } from "./services/pipeline.service";
import { getContactsByColumn, reorderContacts, searchContacts as searchContactsAPI } from "./services/contacts.service";
import { USERS_URL } from "../../../api/Urls";
import { api } from "../../../api/Middleware";

const CONTACTS_PER_PAGE = 10;

// ========== Action Types ==========
const ACTIONS = {
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  SET_PIPELINES: "SET_PIPELINES",
  SET_ACTIVE_PIPELINE: "SET_ACTIVE_PIPELINE",
  SET_COLUMNS: "SET_COLUMNS",
  SET_CONTACTS_BY_COLUMN: "SET_CONTACTS_BY_COLUMN",
  APPEND_CONTACTS: "APPEND_CONTACTS",
  UPDATE_PAGINATION: "UPDATE_PAGINATION",
  SET_LOADING_MORE: "SET_LOADING_MORE",
  MOVE_CONTACT_OPTIMISTIC: "MOVE_CONTACT_OPTIMISTIC",
  REORDER_COLUMNS_OPTIMISTIC: "REORDER_COLUMNS_OPTIMISTIC",
  UPDATE_CONTACT_IN_COLUMN: "UPDATE_CONTACT_IN_COLUMN",
  REMOVE_CONTACTS_FROM_COLUMNS: "REMOVE_CONTACTS_FROM_COLUMNS",
  ADD_CONTACT_TO_COLUMN: "ADD_CONTACT_TO_COLUMN",
  SET_SELECTED_CONTACTS: "SET_SELECTED_CONTACTS",
  TOGGLE_SELECT_CONTACT: "TOGGLE_SELECT_CONTACT",
  CLEAR_SELECTION: "CLEAR_SELECTION",
  SET_VIEW_MODE: "SET_VIEW_MODE",
  SET_SEARCH_QUERY: "SET_SEARCH_QUERY",
  SET_SEARCH_RESULTS: "SET_SEARCH_RESULTS",
  SET_FILTERS: "SET_FILTERS",
  CLEAR_FILTERS: "CLEAR_FILTERS",
  SET_SORT_BY: "SET_SORT_BY",
  SET_SAVED_VIEWS: "SET_SAVED_VIEWS",
  SET_ACTIVE_VIEW: "SET_ACTIVE_VIEW",
  SET_DRAWER: "SET_DRAWER",
  SET_FILTER_PANEL: "SET_FILTER_PANEL",
  SET_TEAM_MEMBERS: "SET_TEAM_MEMBERS",
  ADD_COLUMN: "ADD_COLUMN",
  UPDATE_COLUMN: "UPDATE_COLUMN",
  REMOVE_COLUMN: "REMOVE_COLUMN",
  SAVE_BOARD_SNAPSHOT: "SAVE_BOARD_SNAPSHOT",
  RESTORE_BOARD_SNAPSHOT: "RESTORE_BOARD_SNAPSHOT",
};

// ========== Initial State ==========
const initialState = {
  pipelines: [],
  activePipelineId: null,
  columns: [],
  contactsByColumn: {},
  offsetByColumn: {},
  hasMoreByColumn: {},
  loadingMoreByColumn: {},
  selectedContacts: [],
  activeContactId: null,
  viewMode: "board",
  searchQuery: "",
  searchResults: [],
  filters: {},
  sortBy: null,
  savedViews: [],
  activeViewId: null,
  teamMembers: [],
  drawerOpen: false,
  filterPanelOpen: false,
  loading: true,
  error: null,
  boardSnapshot: null,
};

// ========== Reducer ==========
function crmReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    case ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    case ACTIONS.SET_PIPELINES:
      return { ...state, pipelines: action.payload };
    case ACTIONS.SET_ACTIVE_PIPELINE:
      return { ...state, activePipelineId: action.payload };
    case ACTIONS.SET_COLUMNS:
      return { ...state, columns: action.payload };

    case ACTIONS.SET_CONTACTS_BY_COLUMN: {
      const { columnId, contacts, count, hasNext } = action.payload;
      return {
        ...state,
        contactsByColumn: { ...state.contactsByColumn, [columnId]: contacts },
        offsetByColumn: { ...state.offsetByColumn, [columnId]: CONTACTS_PER_PAGE },
        hasMoreByColumn: { ...state.hasMoreByColumn, [columnId]: hasNext },
      };
    }

    case ACTIONS.APPEND_CONTACTS: {
      const { columnId, contacts, hasNext, newOffset } = action.payload;
      return {
        ...state,
        contactsByColumn: {
          ...state.contactsByColumn,
          [columnId]: [...(state.contactsByColumn[columnId] || []), ...contacts],
        },
        offsetByColumn: { ...state.offsetByColumn, [columnId]: newOffset },
        hasMoreByColumn: { ...state.hasMoreByColumn, [columnId]: hasNext },
        loadingMoreByColumn: { ...state.loadingMoreByColumn, [columnId]: false },
      };
    }

    case ACTIONS.SET_LOADING_MORE:
      return {
        ...state,
        loadingMoreByColumn: { ...state.loadingMoreByColumn, [action.payload.columnId]: action.payload.loading },
      };

    case ACTIONS.MOVE_CONTACT_OPTIMISTIC: {
      const { contactId, srcColId, destColId, destIndex } = action.payload;
      const srcContacts = [...(state.contactsByColumn[srcColId] || [])];
      const contactIdx = srcContacts.findIndex((c) => c.id === contactId);
      if (contactIdx === -1) return state;
      const [contact] = srcContacts.splice(contactIdx, 1);
      contact.column = destColId;

      let destContacts;
      if (srcColId === destColId) {
        destContacts = srcContacts;
      } else {
        destContacts = [...(state.contactsByColumn[destColId] || [])];
      }
      destContacts.splice(destIndex, 0, contact);

      return {
        ...state,
        contactsByColumn: {
          ...state.contactsByColumn,
          [srcColId]: srcColId === destColId ? destContacts : srcContacts,
          [destColId]: destContacts,
        },
      };
    }

    case ACTIONS.REORDER_COLUMNS_OPTIMISTIC:
      return { ...state, columns: action.payload };

    case ACTIONS.UPDATE_CONTACT_IN_COLUMN: {
      const { columnId, contact } = action.payload;
      return {
        ...state,
        contactsByColumn: {
          ...state.contactsByColumn,
          [columnId]: (state.contactsByColumn[columnId] || []).map((c) =>
            c.id === contact.id ? contact : c
          ),
        },
      };
    }

    case ACTIONS.REMOVE_CONTACTS_FROM_COLUMNS: {
      const ids = new Set(action.payload);
      const newMap = {};
      Object.entries(state.contactsByColumn).forEach(([colId, contacts]) => {
        newMap[colId] = contacts.filter((c) => !ids.has(c.id));
      });
      return { ...state, contactsByColumn: newMap, selectedContacts: [] };
    }

    case ACTIONS.ADD_CONTACT_TO_COLUMN: {
      const { columnId, contact } = action.payload;
      const existing = state.contactsByColumn[columnId] || [];
      if (existing.some((c) => c.id === contact.id)) return state;
      return {
        ...state,
        contactsByColumn: {
          ...state.contactsByColumn,
          [columnId]: [...existing, contact],
        },
      };
    }

    case ACTIONS.SET_SELECTED_CONTACTS:
      return { ...state, selectedContacts: action.payload };
    case ACTIONS.TOGGLE_SELECT_CONTACT: {
      const id = action.payload;
      const exists = state.selectedContacts.includes(id);
      return {
        ...state,
        selectedContacts: exists
          ? state.selectedContacts.filter((cid) => cid !== id)
          : [...state.selectedContacts, id],
      };
    }
    case ACTIONS.CLEAR_SELECTION:
      return { ...state, selectedContacts: [] };

    case ACTIONS.SET_VIEW_MODE:
      return { ...state, viewMode: action.payload };
    case ACTIONS.SET_SEARCH_QUERY:
      return { ...state, searchQuery: action.payload };
    case ACTIONS.SET_SEARCH_RESULTS:
      return { ...state, searchResults: action.payload };
    case ACTIONS.SET_FILTERS:
      return { ...state, filters: action.payload };
    case ACTIONS.CLEAR_FILTERS:
      return { ...state, filters: {}, activeViewId: null };
    case ACTIONS.SET_SORT_BY:
      return { ...state, sortBy: action.payload };
    case ACTIONS.SET_SAVED_VIEWS:
      return { ...state, savedViews: action.payload };
    case ACTIONS.SET_ACTIVE_VIEW:
      return { ...state, activeViewId: action.payload };
    case ACTIONS.SET_DRAWER:
      return { ...state, drawerOpen: action.payload.open, activeContactId: action.payload.contactId || null };
    case ACTIONS.SET_FILTER_PANEL:
      return { ...state, filterPanelOpen: action.payload };
    case ACTIONS.SET_TEAM_MEMBERS:
      return { ...state, teamMembers: action.payload };

    case ACTIONS.ADD_COLUMN: {
      const col = action.payload;
      return {
        ...state,
        columns: [...state.columns, col],
        contactsByColumn: { ...state.contactsByColumn, [col.id]: [] },
        offsetByColumn: { ...state.offsetByColumn, [col.id]: 0 },
        hasMoreByColumn: { ...state.hasMoreByColumn, [col.id]: false },
      };
    }
    case ACTIONS.UPDATE_COLUMN:
      return {
        ...state,
        columns: state.columns.map((c) => (c.id === action.payload.id ? { ...c, ...action.payload } : c)),
      };
    case ACTIONS.REMOVE_COLUMN: {
      const colId = action.payload;
      const newCBC = { ...state.contactsByColumn };
      delete newCBC[colId];
      return {
        ...state,
        columns: state.columns.filter((c) => c.id !== colId),
        contactsByColumn: newCBC,
      };
    }

    case ACTIONS.SAVE_BOARD_SNAPSHOT:
      return {
        ...state,
        boardSnapshot: {
          contactsByColumn: { ...state.contactsByColumn },
          offsetByColumn: { ...state.offsetByColumn },
          hasMoreByColumn: { ...state.hasMoreByColumn },
        },
      };

    case ACTIONS.RESTORE_BOARD_SNAPSHOT: {
      if (!state.boardSnapshot) return state;
      return {
        ...state,
        contactsByColumn: state.boardSnapshot.contactsByColumn,
        offsetByColumn: state.boardSnapshot.offsetByColumn,
        hasMoreByColumn: state.boardSnapshot.hasMoreByColumn,
        boardSnapshot: null,
      };
    }

    default:
      return state;
  }
}

// ========== Context ==========
const CRMContext = createContext(null);

export function CRMProvider({ children }) {
  const [state, dispatch] = useReducer(crmReducer, initialState);
  const boardAbortRef = useRef(null);

  const showToast = useCallback((title, icon = "success") => {
    Swal.fire({ title, icon, toast: true, position: "bottom-end", showConfirmButton: false, timer: 3000 });
  }, []);

  // Load pipelines
  const loadPipelines = useCallback(async () => {
    try {
      const data = await getPipelines();
      const pipelines = data.results || data || [];
      dispatch({ type: ACTIONS.SET_PIPELINES, payload: pipelines });
      if (pipelines.length > 0) {
        const defaultPipeline = pipelines.find((p) => p.is_default) || pipelines[0];
        dispatch({ type: ACTIONS.SET_ACTIVE_PIPELINE, payload: defaultPipeline.id });
        return defaultPipeline.id;
      }
      return null;
    } catch (err) {
      console.error("Error loading pipelines:", err);
      dispatch({ type: ACTIONS.SET_ERROR, payload: "Erro ao carregar pipelines." });
      return null;
    }
  }, []);

  // Load columns + contacts for a pipeline (unified endpoint)
  const loadColumnsAndContacts = useCallback(async (pipelineId, filters = {}) => {
    // Cancel any previous in-flight board request
    if (boardAbortRef.current) {
      boardAbortRef.current.abort();
    }
    const abortController = new AbortController();
    boardAbortRef.current = abortController;

    try {
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      const data = await getBoardData(pipelineId, CONTACTS_PER_PAGE, abortController.signal, filters);
      const columnsWithContacts = data.results || [];

      // Extract columns (without contacts field)
      const cols = columnsWithContacts.map(({ contacts, contacts_offset, contacts_has_next, ...col }) => col);
      dispatch({ type: ACTIONS.SET_COLUMNS, payload: cols });

      // Extract contacts per column
      columnsWithContacts.forEach((col) => {
        const contacts = col.contacts || [];
        dispatch({
          type: ACTIONS.SET_CONTACTS_BY_COLUMN,
          payload: {
            columnId: col.id,
            contacts,
            count: col.contacts_count || contacts.length,
            hasNext: col.contacts_has_next || false,
          },
        });
      });

      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    } catch (err) {
      // Silently handle abort/cancel errors
      if (err?.name === 'AbortError' || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
        return;
      }
      console.error("Error loading board:", err);
      dispatch({ type: ACTIONS.SET_ERROR, payload: "Erro ao carregar colunas e contatos." });
    }
  }, []);

  // Load more contacts for a column (infinite scroll)
  const loadMoreContacts = useCallback(
    async (columnId) => {
      if (state.loadingMoreByColumn[columnId] || !state.hasMoreByColumn[columnId]) return;
      dispatch({ type: ACTIONS.SET_LOADING_MORE, payload: { columnId, loading: true } });
      try {
        const offset = state.offsetByColumn[columnId] || CONTACTS_PER_PAGE;
        const resp = await getContactsByColumn(columnId, offset, CONTACTS_PER_PAGE, state.filters);
        const contacts = resp.results || [];
        dispatch({
          type: ACTIONS.APPEND_CONTACTS,
          payload: { columnId, contacts, hasNext: resp.has_next || false, newOffset: offset + CONTACTS_PER_PAGE },
        });
      } catch {
        dispatch({ type: ACTIONS.SET_LOADING_MORE, payload: { columnId, loading: false } });
      }
    },
    [state.loadingMoreByColumn, state.hasMoreByColumn, state.offsetByColumn, state.filters]
  );

  // Switch pipeline
  const switchPipeline = useCallback(
    async (pipelineId) => {
      dispatch({ type: ACTIONS.SET_ACTIVE_PIPELINE, payload: pipelineId });
      dispatch({ type: ACTIONS.CLEAR_SELECTION });
      dispatch({ type: ACTIONS.SET_DRAWER, payload: { open: false } });
      await loadColumnsAndContacts(pipelineId);
    },
    [loadColumnsAndContacts]
  );

  // Move contact — optimistic local update only (used during drag hover)
  const moveContactLocal = useCallback((contactId, srcColId, destColId, destIndex) => {
    dispatch({
      type: ACTIONS.MOVE_CONTACT_OPTIMISTIC,
      payload: { contactId, srcColId, destColId, destIndex },
    });
  }, []);

  // Move contact — persist to API (used on drop)
  const moveContactAPI = useCallback(
    async (contactId, destColId, destIndex) => {
      try {
        const resp = await reorderContacts([
          { id: contactId, columnId: destColId, order: destIndex },
        ]);
        const converted = resp?.auto_converted_contact_ids || [];
        if (converted.length > 0) {
          showToast(
            "Contato convertido em cliente automaticamente!",
            "success",
          );
          // Recarrega para sincronizar badge "Cliente" no card
          await loadColumnsAndContacts(state.activePipelineId, state.filters);
        }
      } catch {
        showToast("Erro ao mover contato.", "error");
        await loadColumnsAndContacts(state.activePipelineId, state.filters);
      }
    },
    [state.activePipelineId, state.filters, loadColumnsAndContacts, showToast]
  );

  // Move contact (dispatch + API in one call, kept for backward compat)
  const moveContact = useCallback(
    async (contactId, srcColId, destColId, destIndex) => {
      moveContactLocal(contactId, srcColId, destColId, destIndex);
      await moveContactAPI(contactId, destColId, destIndex);
    },
    [moveContactLocal, moveContactAPI]
  );

  // Reorder columns — optimistic local update only (used during drag hover)
  const reorderColumnsLocal = useCallback((newColumns) => {
    dispatch({ type: ACTIONS.REORDER_COLUMNS_OPTIMISTIC, payload: newColumns });
  }, []);

  // Reorder columns — persist to API (used on drop)
  const reorderColumnsAPICall = useCallback(
    async (newColumns) => {
      try {
        const items = newColumns.map((col, idx) => ({ id: col.id, order: idx }));
        await reorderColumnsAPI(items);
      } catch {
        showToast("Erro ao reordenar colunas.", "error");
        await loadColumnsAndContacts(state.activePipelineId, state.filters);
      }
    },
    [state.activePipelineId, state.filters, loadColumnsAndContacts, showToast]
  );

  // Reorder columns (dispatch + API in one call)
  const reorderColumnsAction = useCallback(
    async (newColumns) => {
      reorderColumnsLocal(newColumns);
      await reorderColumnsAPICall(newColumns);
    },
    [reorderColumnsLocal, reorderColumnsAPICall]
  );

  // Selection
  const toggleSelectContact = useCallback((id) => {
    dispatch({ type: ACTIONS.TOGGLE_SELECT_CONTACT, payload: id });
  }, []);
  const clearSelection = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_SELECTION });
  }, []);

  // View mode
  const setViewMode = useCallback((mode) => {
    dispatch({ type: ACTIONS.SET_VIEW_MODE, payload: mode });
  }, []);

  // Search
  const setSearchQuery = useCallback((q) => {
    dispatch({ type: ACTIONS.SET_SEARCH_QUERY, payload: q });
  }, []);

  const performSearch = useCallback(
    async (query) => {
      if (!query || query.length < 2) {
        dispatch({ type: ACTIONS.SET_SEARCH_RESULTS, payload: [] });
        // Restore snapshot instead of reloading from server
        dispatch({ type: ACTIONS.RESTORE_BOARD_SNAPSHOT });
        return;
      }
      try {
        // Save snapshot before first search
        if (!state.boardSnapshot) {
          dispatch({ type: ACTIONS.SAVE_BOARD_SNAPSHOT });
        }

        const resp = await searchContactsAPI(query, state.activePipelineId);
        const results = resp.results || [];
        dispatch({ type: ACTIONS.SET_SEARCH_RESULTS, payload: results });

        // Group search results by column and update contactsByColumn for the board
        const grouped = {};
        state.columns.forEach((col) => { grouped[col.id] = []; });
        results.forEach((contact) => {
          const colId = contact.column;
          if (grouped[colId]) {
            grouped[colId].push(contact);
          }
        });
        Object.entries(grouped).forEach(([columnId, contacts]) => {
          dispatch({
            type: ACTIONS.SET_CONTACTS_BY_COLUMN,
            payload: { columnId: parseInt(columnId), contacts, count: contacts.length, hasNext: false },
          });
        });
      } catch {
        dispatch({ type: ACTIONS.SET_SEARCH_RESULTS, payload: [] });
      }
    },
    [state.activePipelineId, state.columns, state.boardSnapshot]
  );

  // Filters
  const setFilters = useCallback((f) => {
    dispatch({ type: ACTIONS.SET_FILTERS, payload: f });
  }, []);
  const clearFilters = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_FILTERS });
  }, []);
  const setSortBy = useCallback((s) => {
    dispatch({ type: ACTIONS.SET_SORT_BY, payload: s });
  }, []);

  // Saved views
  const setSavedViews = useCallback((views) => {
    dispatch({ type: ACTIONS.SET_SAVED_VIEWS, payload: views });
  }, []);
  const setActiveView = useCallback((viewId) => {
    dispatch({ type: ACTIONS.SET_ACTIVE_VIEW, payload: viewId });
  }, []);

  // Drawer
  const openDrawer = useCallback((contactId) => {
    dispatch({ type: ACTIONS.SET_DRAWER, payload: { open: true, contactId } });
  }, []);
  const closeDrawer = useCallback(() => {
    dispatch({ type: ACTIONS.SET_DRAWER, payload: { open: false } });
  }, []);

  // Filter panel
  const toggleFilterPanel = useCallback(() => {
    dispatch({ type: ACTIONS.SET_FILTER_PANEL, payload: !state.filterPanelOpen });
  }, [state.filterPanelOpen]);

  // Team members
  const loadTeamMembers = useCallback(async () => {
    try {
      const resp = await api.get(USERS_URL);
      const users = resp.data.results || resp.data || [];
      dispatch({ type: ACTIONS.SET_TEAM_MEMBERS, payload: users });
    } catch {
      // silent fail
    }
  }, []);

  // Refresh board (accepts optional filters override for same-tick calls)
  const refreshBoard = useCallback(async (filtersOverride) => {
    if (state.activePipelineId) {
      const filters = filtersOverride !== undefined ? filtersOverride : state.filters;
      await loadColumnsAndContacts(state.activePipelineId, filters);
    }
  }, [state.activePipelineId, state.filters, loadColumnsAndContacts]);

  // Column mutations (local state)
  const addColumnToState = useCallback((col) => {
    dispatch({ type: ACTIONS.ADD_COLUMN, payload: col });
  }, []);
  const updateColumnInState = useCallback((col) => {
    dispatch({ type: ACTIONS.UPDATE_COLUMN, payload: col });
  }, []);
  const removeColumnFromState = useCallback((colId) => {
    dispatch({ type: ACTIONS.REMOVE_COLUMN, payload: colId });
  }, []);

  // Contact mutations (local state)
  const updateContactInColumn = useCallback((columnId, contact) => {
    dispatch({ type: ACTIONS.UPDATE_CONTACT_IN_COLUMN, payload: { columnId, contact } });
  }, []);
  const addContactToColumn = useCallback((columnId, contact) => {
    dispatch({ type: ACTIONS.ADD_CONTACT_TO_COLUMN, payload: { columnId, contact } });
  }, []);
  const removeContactsFromColumns = useCallback((ids) => {
    dispatch({ type: ACTIONS.REMOVE_CONTACTS_FROM_COLUMNS, payload: ids });
  }, []);

  const value = {
    state,
    dispatch,
    loadPipelines,
    loadColumnsAndContacts,
    loadMoreContacts,
    switchPipeline,
    moveContact,
    moveContactLocal,
    moveContactAPI,
    reorderColumns: reorderColumnsAction,
    reorderColumnsLocal,
    reorderColumnsAPICall,
    toggleSelectContact,
    clearSelection,
    setViewMode,
    setSearchQuery,
    performSearch,
    setFilters,
    clearFilters,
    setSortBy,
    setSavedViews,
    setActiveView,
    openDrawer,
    closeDrawer,
    toggleFilterPanel,
    loadTeamMembers,
    refreshBoard,
    addColumnToState,
    updateColumnInState,
    removeColumnFromState,
    updateContactInColumn,
    addContactToColumn,
    removeContactsFromColumns,
    showToast,
  };

  return <CRMContext.Provider value={value}>{children}</CRMContext.Provider>;
}

export function useCRM() {
  const context = useContext(CRMContext);
  if (!context) throw new Error("useCRM must be used within CRMProvider");
  return context;
}

export default CRMContext;
